import React, { useEffect, useState } from 'react';
import './EditYAxisModal.scss';
import { yAxisInfoArray, yAxisOverrides } from '../../utilities/chartUtils';
import { RangeSlider } from './RangeSlider';
import Loader from '../../features/common/Loader/Loader';
import { createPortal } from 'react-dom';

interface ExistingColumn {
  id: string;
  Header: string;
  isVisible?: boolean;
}

interface EditYAxisModalProps {
  columnsData: ExistingColumn[];
  onClose?: () => void;
  onYAxisApply?: () => void;
}

export default function EditYAxisModal({ columnsData, onClose, onYAxisApply }: EditYAxisModalProps) {
  // Map input columns to simpler data structure
  const columns = columnsData.map(({ id, Header, isVisible }) => ({
    id,
    name: Header,
    isVisible,
  }));

  const [currentValues, setCurrentValues] = useState<Record<string, { min: number; max: number }>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [resetSignal, setResetSignal] = useState<Record<string, number>>({});
  //force re-render when chart data is loaded
  const [chartReady, setChartReady] = useState(!!(yAxisInfoArray && yAxisInfoArray.length > 0));

  // Filter columns by visibility and search term
  const filteredYAxisData = columns.filter(
    (col) => col.isVisible !== false && col.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleValueChange = (id: string, min: number, max: number) => {
    setCurrentValues((prev) => ({ ...prev, [id]: { min, max } }));
  };

  const handleReset = (id: string, name: string) => {
    delete yAxisOverrides[id];
    const yAxisInfo = yAxisInfoArray.find((item) => item.title === name);

    setCurrentValues((prev) => ({
      ...prev,
      [id]: { min: yAxisInfo?.min ?? 0, max: yAxisInfo?.max ?? 100 },
    }));

    setResetSignal((prev) => ({
      ...prev,
      [id]: (prev[id] || 0) + 1,
    }));

    onYAxisApply?.();
  };

  const handleApply = () => {
    Object.entries(currentValues).forEach(([id, { min, max }]) => {
      yAxisOverrides[id] = { min, max };
    });
    onYAxisApply?.();
    onClose?.();
  };

  const handleCancel = () => {
    onClose?.();
  };

  useEffect(() => {
    if (chartReady) return;
    const interval = setInterval(() => {
      if (yAxisInfoArray && yAxisInfoArray.length > 0) {
        setChartReady(true);
        clearInterval(interval);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [chartReady]);

  const isLoading = !chartReady;

  return createPortal(
    <div className='main-container-panel'>
      <div className='main-container__wrapper'>
        <header className='header'>
          <div className='header__logo-container'>
            <div className='header__logo-icon'>
              <div className='header__logo-icon-inner cursor-pointer' onClick={onClose} />
            </div>
            <div className='header__logo-text'>
              <h4 className='column-header'>Edit Y-axis</h4>
            </div>
          </div>
        </header>

        <section className='toolbar edit-y-axis-toolbar'>
          <div className='toolbar__search-container'>
            <div className='toolbar__search-input-container'>
              <div className='toolbar__search-icon' />
              <div className='toolbar__search-input-wrapper'>
                <input
                  type='text'
                  className='toolbar__search-input'
                  placeholder='Search'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>

        <div className='main-content'>
          <div className='filter-panel'>
            {isLoading ? (
              <div className='y-axis-loader'>
                <Loader />
              </div>
            ) : (
              filteredYAxisData.map((data, index) => {
                const yAxisInfo = yAxisInfoArray.find((item) => item.title === data.name);
                const override = yAxisOverrides[data.id];
                const isDisabled = !yAxisInfo;
                const originalMin = yAxisInfo?.min ?? 0;
                const originalMax = yAxisInfo?.max ?? 0;
                const value = isDisabled
                  ? { min: 0, max: 0 }
                  : currentValues[data.id] || override || { min: originalMin, max: originalMax };

                return (
                  <RangeSlider
                    key={data.id}
                    data={{
                      id: data.id,
                      label: data.name,
                      defaultMin: originalMin,
                      defaultMax: originalMax,
                      unit: '',
                    }}
                    minValue={value.min}
                    maxValue={value.max}
                    index={index + 1}
                    onValueChange={handleValueChange}
                    onReset={() => handleReset(data.id, data.name)}
                    resetSignal={resetSignal[data.id] || 0}
                    disabled={isDisabled}
                  />
                );
              })
            )}
          </div>
        </div>

        <footer className='footer' style={{ zIndex: '99' }}>
          <div className='footer__container edit-y-axis-footer'>
            <button
              analytics-id='edit-y-axis-footer-cancel-btn'
              className='footer__cancel-button'
              onClick={handleCancel}
            >
              <span className='footer__cancel-button-text'>Cancel</span>
            </button>
            <button analytics-id='edit-y-axis-footer-apply-btn' className='footer__apply-button' onClick={handleApply}>
              <span className='footer__apply-button-text'>Apply</span>
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.querySelector('.outlet-container') || document.body,
  );
}
